import React, { useState, useRef, useEffect } from 'react';
import { X, LogOut, Mail, ShieldCheck, Sun, Moon, Monitor, Sparkles, Upload, Trash2, Check, Loader2, type LucideIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme, type ThemeMode } from '../../hooks/useTheme';
import { UserAvatar } from '../common/UserAvatar';

import { ImageCropperModal } from './ImageCropperModal';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({ isOpen, onClose }) => {
  const { user, logout, updateProfile, uploadAvatar, removeAvatar } = useAuth();
  const { theme, setTheme } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [isSavingName, setIsSavingName] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cropping State
  const [selectedCropFile, setSelectedCropFile] = useState<File | null>(null);
  const [isCropperOpen, setIsCropperOpen] = useState<boolean>(false);

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName);
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleLogout = () => {
    logout();
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/') || file.type.includes('svg')) {
      setError('Please select a valid raster image file (PNG, JPG, WebP, GIF)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    setError(null);
    setSelectedCropFile(file);
    setIsCropperOpen(true);
  };

  const handleCropComplete = async (croppedFile: File) => {
    try {
      setIsUploadingAvatar(true);
      setError(null);
      await uploadAvatar(croppedFile);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message;
      setError(serverMsg || 'Failed to upload avatar');
    } finally {
      setIsUploadingAvatar(false);
      setSelectedCropFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      setIsUploadingAvatar(true);
      setError(null);
      await removeAvatar();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message;
      setError(serverMsg || 'Failed to remove avatar');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSaveDisplayName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim() || displayName.trim() === user.displayName) return;

    try {
      setIsSavingName(true);
      setError(null);
      await updateProfile({ displayName: displayName.trim() });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message;
      setError(serverMsg || 'Failed to update display name');
    } finally {
      setIsSavingName(false);
    }
  };

  const themeOptions: Array<{ id: ThemeMode; label: string; icon: LucideIcon }> = [
    { id: 'light', label: 'Light', icon: Sun },
    { id: 'dark', label: 'Dark', icon: Moon },
    { id: 'system', label: 'Auto', icon: Monitor },
    { id: 'custom', label: 'Custom', icon: Sparkles },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-card border border-border rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <span className="font-bold text-base text-foreground">User Profile</span>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 flex flex-col items-center text-center space-y-4">
          {error && (
            <div className="w-full p-2.5 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-xs font-medium">
              {error}
            </div>
          )}

          {saveSuccess && (
            <div className="w-full p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-medium flex items-center justify-center gap-1.5">
              <Check className="h-3.5 w-3.5" />
              <span>Profile updated successfully</span>
            </div>
          )}

          {/* User Avatar with Edit / Remove actions */}
          <div className="flex flex-col items-center gap-2.5">
            <div className="relative group">
              <UserAvatar
                userId={user.id}
                displayName={user.displayName}
                avatarUrl={user.avatarUrl}
                size="2xl"
                className="shadow-lg ring-4 ring-card"
              />

              {isUploadingAvatar && (
                <div className="absolute inset-0 rounded-3xl bg-black/50 backdrop-blur-2xs flex items-center justify-center text-white">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              )}
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
            />

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={isUploadingAvatar}
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-1.5 bg-muted/70 hover:bg-muted text-foreground border border-border rounded-xl text-xs font-semibold inline-flex items-center gap-1.5 transition shadow-2xs disabled:opacity-50"
              >
                <Upload className="h-3.5 w-3.5 text-primary" />
                <span>{user.avatarUrl ? 'Change Photo' : 'Upload Photo'}</span>
              </button>

              {user.avatarUrl && (
                <button
                  type="button"
                  disabled={isUploadingAvatar}
                  onClick={handleRemoveAvatar}
                  className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 border border-border rounded-xl transition disabled:opacity-50"
                  title="Remove Photo"
                  aria-label="Remove Photo"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Editable Display Name Form */}
          <form onSubmit={handleSaveDisplayName} className="w-full text-left space-y-2">
            <div>
              <label className="block text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-1">
                Display Name
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  required
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="flex-1 h-9 px-3 bg-background border border-border rounded-xl text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                />
                {displayName.trim() !== user.displayName && (
                  <button
                    type="submit"
                    disabled={isSavingName || !displayName.trim()}
                    className="h-9 px-3 bg-primary text-primary-foreground text-xs font-bold rounded-xl hover:bg-primary/90 transition disabled:opacity-50 flex items-center gap-1 shrink-0"
                  >
                    {isSavingName ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-1">
                Email Address
              </label>
              <div className="h-9 px-3 bg-muted/40 border border-border rounded-xl text-xs text-muted-foreground flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate">{user.email}</span>
              </div>
            </div>
          </form>

          {/* Theme Quick Selector */}
          <div className="w-full py-2.5 px-3 bg-muted/40 rounded-2xl border border-border flex flex-col gap-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground font-medium">Appearance</span>
              <span className="text-[11px] font-semibold text-primary capitalize">{theme}</span>
            </div>
            <div className="grid grid-cols-4 gap-1 bg-background/80 p-1 rounded-xl border border-border/60">
              {themeOptions.map((opt) => {
                const Icon = opt.icon;
                const isSelected = theme === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setTheme(opt.id)}
                    className={`flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg text-xs font-semibold transition ${
                      isSelected
                        ? 'bg-primary text-primary-foreground shadow-xs'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="w-full py-2 px-3 bg-muted/30 rounded-xl border border-border flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Account Status</span>
            <span className="font-semibold text-emerald-500 flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5" /> Active
            </span>
          </div>

          <div className="w-full pt-1">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-destructive/10 hover:bg-destructive/20 text-destructive text-sm font-semibold transition cursor-pointer"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Interactive Image Cropper Modal */}
      {isCropperOpen && selectedCropFile && (
        <ImageCropperModal
          isOpen={isCropperOpen}
          imageFile={selectedCropFile}
          cropShape="circle"
          title="Crop Profile Picture"
          onClose={() => {
            setIsCropperOpen(false);
            setSelectedCropFile(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
};
